// Libraries
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useMountEffect, useResponsive, useState} from '@supermove/hooks';
import {InvoiceModel, ProjectModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import TextTooltip from '@shared/design/components/TextTooltip';
import FinalizedInvoiceCallout from '@shared/modules/Accounting/components/Invoice/FinalizedInvoiceCallout';
import InvoiceCard from '@shared/modules/Accounting/components/Invoice/InvoiceCard';
import InvoiceStatusBadge from '@shared/modules/Accounting/components/Invoice/InvoiceStatusBadge';
import InvoiceSummary from '@shared/modules/Accounting/components/Invoice/InvoiceSummary';
import ProjectBilling from '@shared/modules/Project/components/Billing/ProjectBilling';
import ProjectBillingActionButtons from '@shared/modules/Project/components/Billing/ProjectBillingActionButtons';
import ProjectDetailsBlockHeader from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';
import {ProjectBlockKindType} from '@shared/modules/Project/enums/ProjectBlockKind';

const BLOCK_TITLE = 'Invoice';

const View = Styled.View``;

const Row = Styled.View`
  flex-direction: row;
`;

const SkeletonBody = () => {
  return (
    <React.Fragment>
      <SkeletonLoader isFullWidth height={92} />
      <Space height={16} />
      <ProjectBilling.SkeletonLoader />
    </React.Fragment>
  );
};

const SkeletonComponent = () => {
  return (
    <ActionPanel
      BodyComponent={SkeletonBody}
      ActionButtonComponent={() => (
        <Row>
          <ProjectBillingActionButtons.SkeletonLoader />
          <Space width={12} />
          <SkeletonLoader height={SkeletonLoader.HEIGHT.ButtonSmall} width={24} />
        </Row>
      )}
      title={BLOCK_TITLE}
      style={{width: '100%'}}
    />
  );
};

const BodyComponent = ({
  project,
  refetch,
  pageRefetch,
  descriptionField,
  getTruncatedKey,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  viewer,
}: {
  project: ProjectModel;
  refetch: () => void;
  pageRefetch: () => void;
  descriptionField: string;
  getTruncatedKey: (args: {billItemId: string}) => string;
  truncated: Record<string, boolean>;
  setTruncated: (truncated: Record<string, boolean>) => void;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: (enabledToggle: Record<string, boolean>) => void;
  viewer: UserModel;
}) => {
  return (
    <React.Fragment>
      <FinalizedInvoiceCallout
        invoice={project.mainMoveInvoice}
        refetch={refetch}
        isAuthorizedAccountingActions={viewer.isAuthorizedAccountingActions}
      />
      <InvoiceSummary invoice={project.mainMoveInvoice} />
      <Space height={16} />
      <ProjectBilling
        project={project}
        refetch={refetch}
        pageRefetch={pageRefetch}
        descriptionField={descriptionField}
        getTruncatedKey={getTruncatedKey}
        truncated={truncated}
        setTruncated={setTruncated}
        enabledToggle={enabledToggle}
        setEnabledToggle={setEnabledToggle}
      />
    </React.Fragment>
  );
};

const HeaderStatusBadge = ({invoice}: {invoice: InvoiceModel}) => {
  return (
    <React.Fragment>
      {invoice.hasEmailSendError && (
        <React.Fragment>
          <TextTooltip isEnabledMobileToast={false} text={'Invoice failed to send'}>
            <View style={{justifyContent: 'center'}}>
              <Icon source={Icon.ExclamationTriangle} size={16} color={colors.red.warning} />
            </View>
          </TextTooltip>
          <Space width={12} />
        </React.Fragment>
      )}
      <InvoiceStatusBadge invoice={invoice} />
    </React.Fragment>
  );
};

const ProjectDetailsInvoiceBlockContent = ({
  project,
  refetch,
  pageRefetch,
  refetchAndReset,
  handleUpdateBlock,
  descriptionField,
  getTruncatedKey,
  truncated,
  setTruncated,
  enabledToggle,
  setEnabledToggle,
  viewer,
}: {
  project: ProjectModel;
  refetch: () => void;
  pageRefetch: () => void;
  refetchAndReset: () => void;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
  descriptionField: string;
  getTruncatedKey: (args: {billItemId: string}) => string;
  truncated: Record<string, boolean>;
  setTruncated: (truncated: Record<string, boolean>) => void;
  enabledToggle: Record<string, boolean>;
  setEnabledToggle: (enabledToggle: Record<string, boolean>) => void;
  viewer: UserModel;
}) => {
  // Set state for truncated and enabledToggle after data has been queried.
  useMountEffect(() => {
    const newTruncated: Record<string, boolean> = {};
    const newEnabledToggle: Record<string, boolean> = {};

    project.bills.forEach((bill) => {
      bill.billItems.forEach((billItem) => {
        const truncatedKey = getTruncatedKey({billItemId: billItem.id});

        // Whenever component is mounted, initialize expand/collapse state for new bill item
        // descriptions and keep existing value for existing descriptions.
        if (Object.prototype.hasOwnProperty.call(truncated, truncatedKey)) {
          newTruncated[truncatedKey] = truncated[truncatedKey];
        } else {
          newTruncated[truncatedKey] = true;
        }

        if (Object.prototype.hasOwnProperty.call(enabledToggle, truncatedKey)) {
          newEnabledToggle[truncatedKey] = enabledToggle[truncatedKey];
        } else {
          newEnabledToggle[truncatedKey] = false;
        }
      });
    });

    setTruncated(newTruncated);
    setEnabledToggle(newEnabledToggle);
  });

  return (
    <ActionPanel
      BodyComponent={BodyComponent}
      bodyComponentProps={{
        project,
        refetch,
        pageRefetch,
        descriptionField,
        getTruncatedKey,
        truncated,
        setTruncated,
        enabledToggle,
        setEnabledToggle,
        viewer,
      }}
      ActionButtonComponent={ProjectBillingActionButtons}
      actionButtonComponentProps={{
        project,
        refetch,
        pageRefetch,
        refetchAndReset,
        handleUpdateBlock,
      }}
      title={`Invoice ${project.mainMoveInvoice.identifier}`}
      HeaderIconComponent={HeaderStatusBadge}
      headerIconComponentProps={{invoice: project.mainMoveInvoice}}
      style={{width: '100%'}}
    />
  );
};

const ProjectDetailsInvoiceBlockContentMobile = ({project}: {project: ProjectModel}) => {
  const {mainMoveInvoice} = project;

  return (
    <React.Fragment>
      <ProjectDetailsBlockHeader title={BLOCK_TITLE} />
      <InvoiceCard invoice={mainMoveInvoice} />
    </React.Fragment>
  );
};

const ProjectDetailsInvoiceBlock = ({
  projectUuid,
  project,
  index,
  handleUpdateBlock,
  pageRefetch,
  refetchAndReset,
  isWidget = true,
}: {
  projectUuid: string;
  project?: ProjectModel;
  index: number;
  handleUpdateBlock: (block: ProjectBlockKindType) => void;
  pageRefetch: () => void;
  refetchAndReset: () => void;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();

  // Note(shawn): Defining expand/collapse state for bill item descriptions here to avoid state
  // being reset on remounts whenever page height changes.
  const descriptionField = 'description';
  const getTruncatedKey = ({billItemId}: {billItemId: string}) =>
    `BILL_ITEM_${billItemId}_${descriptionField.toUpperCase()}`;
  const [truncated, setTruncated] = useState({});
  const [enabledToggle, setEnabledToggle] = useState({});

  return (
    <ProjectDetailsBlockWrapper
      key={`${projectUuid}_${project?.billsHash}_${project?.jobStopsHash}_${project?.totalRevenue}_${project?.valuationCoverage?.updatedAt}`}
      query={ProjectDetailsInvoiceBlock.query}
      queryVariables={{projectUuid}}
      index={index}
      SkeletonComponent={SkeletonComponent}
    >
      {({
        data,
        refetch,
      }: {
        data: {project: ProjectModel; viewer: UserModel};
        refetch: () => void;
      }) => {
        return (
          <React.Fragment>
            {responsive.desktop && !isWidget ? (
              <ProjectDetailsInvoiceBlockContent
                project={data.project}
                refetch={refetch}
                pageRefetch={pageRefetch}
                refetchAndReset={refetchAndReset}
                handleUpdateBlock={handleUpdateBlock}
                descriptionField={descriptionField}
                getTruncatedKey={getTruncatedKey}
                truncated={truncated}
                setTruncated={setTruncated}
                enabledToggle={enabledToggle}
                setEnabledToggle={setEnabledToggle}
                viewer={data.viewer}
              />
            ) : (
              <ProjectDetailsInvoiceBlockContentMobile project={data.project} />
            )}
          </React.Fragment>
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsInvoiceBlock.fragment = gql`
  fragment ProjectDetailsInvoiceBlock on Project {
    id
    billsHash
    jobStopsHash
    totalRevenue
    valuationCoverage {
      id
      updatedAt
    }
  }
`;

ProjectDetailsInvoiceBlock.query = gql`
  ${ProjectBillingActionButtons.fragment}
  ${FinalizedInvoiceCallout.fragment}
  ${InvoiceSummary.fragment}
  ${ProjectBilling.fragment}
  ${InvoiceCard.fragment}

  query ProjectDetailsInvoiceBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      uuid
      billsHash
      mainMoveInvoice {
        id
        identifier
        ...FinalizedInvoiceCallout
        ...InvoiceSummary
        ...InvoiceCard
      }
      bills {
        id
        billItems {
          id
        }
      }
      ...ProjectBillingActionButtons
      ...ProjectBilling
    }
    viewer {
      id
      isAuthorizedAccountingActions
    }
  }
`;

export default ProjectDetailsInvoiceBlock;
